/**
 * "Nothing works and everything is frozen."
 *
 * Switch wifi off in Settings, come back to the app, tap a tab: the tap does
 * nothing for the better part of a minute. The app is not frozen — it is waiting
 * on a client-side (RSC) navigation request that will never answer, because
 * losing wifi on iOS does not fail in-flight fetches, it hangs them. Meanwhile
 * the whole offline app (the precached shell, the cached jobs, the queue) is
 * sitting on the device with nobody asking for it.
 *
 * So once connectivity.ts has PROVEN there is no network, we take the user there
 * ourselves: a full-document navigation, which the service worker answers from
 * the precache with the offline shell (app/sw.ts `fieldNavigationRoute`). The
 * shell then does its own gate — a live session if one can be confirmed,
 * otherwise the PIN.
 *
 * This module is the decision, kept pure and testable. components/
 * OfflineAutoSwitch.tsx is the wiring.
 */

import type { LocationLike } from "./back-online";

/**
 * Where to go to work offline from `loc`, or `null` for "leave this page alone".
 *
 * Deliberately narrow. Auto-switching is a good trade in exactly one place: the
 * mobile field app, where the user is a signed-in tech, the data is already on
 * the device, and the alternative is a dead screen. Everywhere else it is a bad
 * one, so those return `null`:
 *
 * - `/offline*`, `/offline-submit` — already the offline app. Switching here
 *   would navigate the shell to itself, forever.
 * - `/submit/*` — public form fill. A client answering a survey has no PIN and no
 *   cached jobs; the offline gate would show them a lock screen instead of their
 *   half-finished form. They don't need it either: answers are held in IndexedDB
 *   and the submission queue takes the submit whether or not there's a network.
 * - `/` and the desktop app — no offline view exists to switch to, and the
 *   service worker deliberately refuses to serve the field shell outside the
 *   field app (see `isFieldNavigation` in app/sw.ts). A banner is the honest
 *   answer there, not a redirect.
 *
 * The target keeps the CURRENT url. The shell is one document served for many
 * paths and re-derives its view from the pathname (OfflineShell `routeFromPath`),
 * so staying put is what keeps the tech on the job they were looking at — and
 * what lets "back online" bring them back to the right page.
 */
export function autoOfflineTargetFromLocation(loc: LocationLike): string | null {
  const { pathname } = loc;
  if (pathname === "/m" || pathname.startsWith("/m/")) {
    return pathname + loc.search;
  }
  return null;
}

/**
 * Is there actually an offline app on this device to switch TO?
 *
 * The switch is a document navigation that only works because the service worker
 * answers it from the precache. On a device where the precache never completed —
 * a stuck-in-`waiting` worker from an older build, an install that failed on one
 * entry, storage evicted — there is nothing to serve, and navigating would trade
 * a stalled page for the browser's offline error screen. That is a worse place to
 * put someone, so we check first and leave them where they are.
 *
 * `ignoreSearch` because the precache keys carry a revision
 * (`/offline?__WB_REVISION__=…`), the same way the offline-check page looks for it.
 */
export async function hasPrecachedOfflineShell(): Promise<boolean> {
  try {
    if (typeof caches === "undefined") return false;
    const hit = await caches.match("/offline", { ignoreSearch: true });
    return !!hit;
  } catch {
    return false;
  }
}

/**
 * Records that this app session already switched, so a switch cannot loop.
 *
 * The loop is real without it: the offline shell is served AT the url it replaced
 * (`/m/jobs`), it mounts the same app-wide watcher, and it is still offline — so
 * the same decision fires again on the same path and reloads the document
 * forever. sessionStorage (not module state) because the switch is a full
 * document load: module state is gone on the other side.
 *
 * The cooldown is per-path and time-boxed rather than permanent: a tech who
 * reconnects, resumes the real page and then walks into a basement an hour later
 * must be switched again.
 */
const SWITCH_KEY = "st:offline-auto-switched";
const SWITCH_COOLDOWN_MS = 60_000;

type SwitchRecord = { path: string; at: number };

function readRecord(): SwitchRecord | null {
  try {
    const raw = window.sessionStorage.getItem(SWITCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SwitchRecord>;
    if (typeof parsed?.path !== "string" || !Number.isFinite(parsed?.at)) return null;
    return { path: parsed.path, at: parsed.at as number };
  } catch {
    // private mode / storage disabled / corrupt value
    return null;
  }
}

/** True when we switched to offline mode for this path moments ago. */
export function didRecentlyAutoSwitch(path: string, now = Date.now()): boolean {
  const rec = readRecord();
  if (!rec) return false;
  return rec.path === path && now - rec.at < SWITCH_COOLDOWN_MS;
}

export function recordAutoSwitch(path: string, now = Date.now()): void {
  try {
    window.sessionStorage.setItem(SWITCH_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // Best-effort. Without it the worst case is one extra reload, which the
    // in-document guard in OfflineAutoSwitch already absorbs.
  }
}

/**
 * Set by the offline documents (the shell layout, the /offline-submit gate) as
 * soon as they mount: "this document IS the offline app".
 *
 * Belt to the cooldown's braces, and the more precise of the two — sessionStorage
 * can be unavailable, and after a reconnect-then-drop the cooldown may legitimately
 * have expired while the document on screen is still the shell.
 */
let isShellDocument = false;

export function markOfflineShellDocument(): void {
  isShellDocument = true;
}

export function isOfflineShellDocument(): boolean {
  return isShellDocument;
}
